import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Globe,
  Server,
  Database,
  Cpu,
  HardDrive,
  ArrowDown,
  ArrowRight,
  Layers,
} from "lucide-react";

const layers = [
  {
    id: "frontend",
    label: "React SPA",
    tech: "React 18 + TypeScript + Vite",
    icon: Globe,
    color: "bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800",
    iconColor: "text-blue-600 dark:text-blue-400",
  },
  {
    id: "api",
    label: "FastAPI",
    tech: "Python 3.12 + SQLAlchemy 2.0 async",
    icon: Server,
    color: "bg-violet-50 dark:bg-violet-950/40 border-violet-200 dark:border-violet-800",
    iconColor: "text-violet-600 dark:text-violet-400",
  },
  {
    id: "db",
    label: "PostgreSQL 15",
    tech: "asyncpg + Alembic migrations",
    icon: Database,
    color: "bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800",
    iconColor: "text-emerald-600 dark:text-emerald-400",
  },
];

const sideServices = [
  {
    id: "redis",
    label: "Redis 7",
    description: "Cache & sessions",
    icon: HardDrive,
    color: "bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800",
    iconColor: "text-red-600 dark:text-red-400",
    connectsTo: "api",
  },
  {
    id: "ai",
    label: "IA Models",
    description: "Gemini + Infomaniak AI",
    icon: Cpu,
    color: "bg-orange-50 dark:bg-orange-950/40 border-orange-200 dark:border-orange-800",
    iconColor: "text-orange-600 dark:text-orange-400",
    connectsTo: "api",
  },
];

const techBadges = [
  { label: "React 18", color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
  { label: "TypeScript", color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
  { label: "Vite", color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
  { label: "shadcn/ui", color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
  { label: "Tailwind CSS", color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
  { label: "React Query", color: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" },
  { label: "FastAPI", color: "bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200" },
  { label: "SQLAlchemy 2.0", color: "bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200" },
  { label: "Pydantic", color: "bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200" },
  { label: "Alembic", color: "bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200" },
  { label: "PostgreSQL 15", color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200" },
  { label: "asyncpg", color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200" },
  { label: "Redis 7", color: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" },
  { label: "Gemini API", color: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200" },
  { label: "Infomaniak AI", color: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200" },
  { label: "Docker", color: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200" },
  { label: "GitHub Actions", color: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200" },
  { label: "Caddy", color: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200" },
];

const legendItems = [
  { label: "Frontend", color: "bg-blue-400" },
  { label: "Backend", color: "bg-violet-400" },
  { label: "Data", color: "bg-emerald-400" },
  { label: "IA", color: "bg-orange-400" },
  { label: "Infra", color: "bg-slate-400" },
];

export default function ArchitectureOverview() {
  return (
    <div className="space-y-8">
      {/* Architecture diagram */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Layers className="h-4 w-4" />
          Stack technique
        </h3>

        <div className="flex gap-6 items-start">
          {/* Main vertical stack */}
          <div className="flex flex-col items-center gap-3 flex-1">
            {layers.map((layer, i) => {
              const Icon = layer.icon;
              return (
                <div key={layer.id} className="w-full flex flex-col items-center">
                  <Card
                    className={`w-full max-w-md border-2 px-4 py-3 ${layer.color}`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={`h-5 w-5 ${layer.iconColor}`} />
                      <div>
                        <p className="text-sm font-semibold">{layer.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {layer.tech}
                        </p>
                      </div>
                    </div>
                  </Card>
                  {i < layers.length - 1 && (
                    <ArrowDown className="h-5 w-5 text-muted-foreground my-1" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Side services */}
          <div className="flex flex-col gap-3 pt-[60px]">
            {sideServices.map((svc) => {
              const Icon = svc.icon;
              return (
                <div key={svc.id} className="flex items-center gap-2">
                  <ArrowRight className="h-4 w-4 text-muted-foreground rotate-180" />
                  <Card
                    className={`border-2 px-3 py-2 min-w-[160px] ${svc.color}`}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className={`h-4 w-4 ${svc.iconColor}`} />
                      <div>
                        <p className="text-sm font-semibold">{svc.label}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {svc.description}
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4">
        {legendItems.map((item) => (
          <div key={item.label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className={`h-2.5 w-2.5 rounded-full ${item.color}`} />
            {item.label}
          </div>
        ))}
      </div>

      {/* Tech badges grid */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-muted-foreground">
          Technologies
        </h3>
        <div className="flex flex-wrap gap-2">
          {techBadges.map((badge) => (
            <Badge
              key={badge.label}
              variant="secondary"
              className={`text-xs ${badge.color}`}
            >
              {badge.label}
            </Badge>
          ))}
        </div>
      </div>

      {/* Deployment flow */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-muted-foreground">
          Deploiement
        </h3>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Badge variant="outline" className="gap-1">
            git push
          </Badge>
          <ArrowRight className="h-3 w-3 text-muted-foreground" />
          <Badge variant="outline" className="gap-1">
            GitHub Actions
          </Badge>
          <ArrowRight className="h-3 w-3 text-muted-foreground" />
          <Badge variant="outline" className="gap-1">
            GHCR (Docker)
          </Badge>
          <ArrowRight className="h-3 w-3 text-muted-foreground" />
          <Badge variant="outline" className="gap-1">
            VPS Infomaniak
          </Badge>
          <ArrowRight className="h-3 w-3 text-muted-foreground" />
          <Badge variant="outline" className="gap-1">
            Caddy (TLS)
          </Badge>
        </div>
        <div className="text-xs text-muted-foreground space-y-0.5">
          <p>
            <span className="font-medium">Production</span> : push main → ports 8000/3000 → ao-epfl.fdapps.ch
          </p>
          <p>
            <span className="font-medium">Dev</span> : push dev → ports 8001/3001 → dev.ao-epfl.fdapps.ch
          </p>
        </div>
      </div>
    </div>
  );
}
